/**
 * `SkeletonRows` - loading-state pulse blocks shown inside `<tbody>` while
 * the first page is fetched.
 *
 * Renders `count` faux rows roughly the height of a real one, using
 * Tailwind's `animate-pulse`. Per 04b: "the FilterBar stays interactive";
 * the skeleton is in-table only, not a full-page overlay.
 *
 * Honours `prefers-reduced-motion` via the motion tokens already collapsed
 * to 0ms in tokens.css.
 */

import { ACCOUNT_COLUMNS } from '../features/accounts/columns'

export interface SkeletonRowsProps {
  /** How many faux rows to draw (defaults to 8 for the first paint). */
  readonly count?: number
  /** Optional descriptive label - defaults to "Loading accounts". */
  readonly label?: string
}

export function SkeletonRows({
  count = 8,
  label = 'Loading accounts',
}: SkeletonRowsProps): JSX.Element {
  const cols = ACCOUNT_COLUMNS.length
  const rows = Array.from({ length: count })
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="w-full">
      <span className="sr-only">{label}</span>
      <div className="divide-y divide-border-default">
        {rows.map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex w-full items-center gap-murmuration-3 px-murmuration-3 py-murmuration-3"
            data-testid="skeleton-row"
          >
            {Array.from({ length: cols }).map((__, colIndex) => (
              <div key={colIndex} className="h-4 flex-1 animate-pulse rounded-sm bg-bg-surface-2" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
